import React, { useState, useEffect, useCallback } from "react";
import { tr } from '@/i18n/tr';
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Store, Building2, Eye, EyeOff, Monitor, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { trackLanding } from "@/utils/landingTrack";
import axios, { API } from "@/lib/api";
import { themeIconUrl } from "@/utils/theme";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import { useAppVersion } from "@/components/VersionBadge";

// Phone (or narrow touch screen) → mobile order pad; otherwise the normal PC POS
const isPhoneDevice = () => {
  try { return window.innerWidth < 900 || /android|iphone|ipad|mobile/i.test(navigator.userAgent); } catch { return false; }
};

export default function LoginPage() {
  const appVer = useAppVersion();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  // 🆕 2026-11-03: a merchant who just registered lands here with tenant_id/username prefilled on the merchant tab
  // (a real visitor typed the new account into the super-admin tab 3× and left).
  const regState = location.state || {};
  const [tenantIdState, setTenantId] = useState(() => { try { return regState.tenant_id || localStorage.getItem("pos_tenant_id") || ""; } catch { return regState.tenant_id || ""; } });
  const [tUsernameState, setTUsername] = useState(regState.username || "");
  const defaultTab = regState.tab || (() => { try { return localStorage.getItem("sx_login_tab") || "tenant"; } catch { return "tenant"; } })();
  const [tPasswordState, setTPassword] = useState("");
  const [showTPassword, setShowTPassword] = useState(false);
  const [deviceVerify, setDeviceVerify] = useState(null); // {email_masked, sent, retry_in}
  const [deviceCode, setDeviceCode] = useState("");
  const [posIdState, setPosId] = useState(() => { try { return localStorage.getItem("pos_tenant_id") || ""; } catch { return ""; } });
  const [posUserState, setPosUser] = useState("");
  const [posPwdState, setPosPwd] = useState("");
  const [showPosPwd, setShowPosPwd] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const { login, tenantLogin, offlineLogin, adoptSession } = useAuth();
  const { t, lang, changeLang } = useLang();
  const navigate = useNavigate();

  // If offline (or desktop app with a saved session), skip the login form
  useEffect(() => {
    const navByRole = (u) => {
      const role = u?.role || "staff";
      if (role === "price_checker" || role === "stock_viewer") navigate("/price-check", { replace: true });
      else if (["cashier", "staff", "store_manager"].includes(role)) navigate("/pos", { replace: true });
      else navigate("/admin", { replace: true });
    };
    // Desktop always boots on /login — restore the saved session instead of
    // asking the merchant to re-type credentials every morning.
    if (window.selloxDesktop) {
      const tok = localStorage.getItem("token");
      const cu = localStorage.getItem("cached_auth_user") || localStorage.getItem("pos_user");
      if (tok && cu) {
        try { navByRole(JSON.parse(cu)); return; } catch {}
      }
    }
    if (!navigator.onLine) {
      const cachedUser = localStorage.getItem("pos_user");
      if (cachedUser) {
        navigate("/pos", { replace: true });
      }
    }
  }, []);

  // only browsers that never logged into a shop count as "visitors" (cashiers logging in daily are not leads)
  const isNewBrowser = () => { try { return !localStorage.getItem("pos_tenant_id") && !localStorage.getItem("cached_auth_user") && !localStorage.getItem("pos_user"); } catch { return false; } };
  useEffect(() => { if (isNewBrowser()) trackLanding('login'); }, []);

  // PWA Install
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  const handleInstall = useCallback(async () => {
    if (installPrompt) { installPrompt.prompt(); const { outcome } = await installPrompt.userChoice; if (outcome === 'accepted') setIsInstalled(true); setInstallPrompt(null); }
  }, [installPrompt]);

  // Offline login: verify against cached credentials and restore session
  const sha256Hex = async (s) => {
    if (!window.crypto?.subtle) return null; // insecure context (plain-http LAN kiosk)
    const d = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
    return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, "0")).join("");
  };
  const saveOfflineCreds = async (tid, uname, pwd) => {
    const h = await sha256Hex(pwd);
    const entry = h ? { tenant_id: tid, username: uname, pwd_sha256: h }
      : { tenant_id: tid, username: uname, password: pwd };
    localStorage.setItem("pos_auth_creds", JSON.stringify(entry)); // legacy single-slot
    try {
      const map = JSON.parse(localStorage.getItem("pos_auth_creds_map") || "{}");
      map[`${(tid || '').toLowerCase()}:${(uname || '').toLowerCase()}`] = entry;
      localStorage.setItem("pos_auth_creds_map", JSON.stringify(map));
    } catch {}
  };
  // Desktop proxy answers 502 + x-sellox-proxy:offline when the cable is out —
  // that has a `response`, so `!error.response` alone misses it.
  const isNetFail = (error) => !error.response ||
    (error.response.status === 502 &&
      (error.response.headers?.["x-sellox-proxy"] === "offline" || error.response.data?.detail === "offline"));

  const tryOfflineLogin = async (tid, uname, pwd) => {
    const key = `${(tid || '').toLowerCase()}:${(uname || '').toLowerCase()}`;
    let cached = null;
    try { cached = (JSON.parse(localStorage.getItem("pos_auth_creds_map") || "{}"))[key] || null; } catch {}
    if (!cached) {
      try { cached = JSON.parse(localStorage.getItem("pos_auth_creds") || "null"); } catch {}
    }
    if (!cached) return false;
    let cachedUser = null, tok = null;
    try {
      const sess = (JSON.parse(localStorage.getItem("pos_offline_sessions") || "{}"))[key];
      if (sess?.user) { cachedUser = sess.user; tok = sess.token; }
    } catch {}
    if (!cachedUser) {
      try {
        const sess = JSON.parse(localStorage.getItem("pos_offline_session") || "null");
        if (sess?.user) { cachedUser = sess.user; tok = sess.token; }
      } catch {}
    }
    if (!cachedUser) {
      const savedUser = localStorage.getItem("pos_user") || localStorage.getItem("cached_auth_user");
      if (!savedUser) return false;
      try { cachedUser = JSON.parse(savedUser); } catch { return false; }
      tok = cachedUser.token || localStorage.getItem("token");
    }
    const idMatch = (cached.tenant_id || '').toLowerCase() === (tid || '').toLowerCase()
      && (cached.username || '').toLowerCase() === (uname || '').toLowerCase();
    const pwdMatch = cached.pwd_sha256
      ? (cached.pwd_sha256 === await sha256Hex(pwd))
      : (cached.password === pwd);
    if (idMatch && pwdMatch) {
      // Restore full auth state (context + storage) so route guards let us in
      offlineLogin(cachedUser, tok);
      try { localStorage.setItem("pos_user", JSON.stringify(cachedUser)); } catch {}
      toast.info(t('offlineMode') || tr('x_e4bb7fff'));
      const role = cachedUser.role || 'staff';
      if (role === 'price_checker' || role === 'stock_viewer') navigate("/price-check", { replace: true });
      else if (role === 'wholesale_clerk') navigate("/admin/wholesale", { replace: true });
      else if (['cashier', 'staff', 'store_manager'].includes(role)) navigate("/pos", { replace: true });
      else navigate("/admin", { replace: true });
      setIsLoading(false);
      return true;
    }
    return false;
  };


  // 👤 Face login (uses last-remembered tenant; user can edit store code in modal)
  const rememberedTenant = () => {
    try {
      const tid = localStorage.getItem("pos_tenant_id");
      if (tid) return tid;
      const cu = JSON.parse(localStorage.getItem("cached_auth_user") || "null");
      if (cu?.tenant_id) return cu.tenant_id;
      const sess = JSON.parse(localStorage.getItem("pos_offline_session") || "null");
      if (sess?.user?.tenant_id) return sess.user.tenant_id;
      const creds = JSON.parse(localStorage.getItem("pos_auth_creds") || "null");
      if (creds?.tenant_id) return creds.tenant_id;
    } catch {}
    return "";
  };

  // Chrome/Edge autofill fills the boxes but React state can still be "" on the very first
  // submit (401 "用户名或密码错误" → boss has to log in twice). Read the DOM values as fallback.
  const formVal = (e, name, stateVal) => stateVal || String(new FormData(e.currentTarget).get(name) || "").trim();

  // 🔐 shared 6-digit code box (tenant owner/admin + super admin): resend re-runs the login without a code → server mails again
  const codeBox = (resend) => (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="device-verify-box">
      <p className="text-xs text-amber-200">{t('deviceVerifyTitle')}{deviceVerify.email_masked ? ` ${deviceVerify.email_masked}` : ""}</p>
      <Input name="device_code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={deviceCode} onChange={(e) => setDeviceCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="bg-slate-700/50 border-amber-500/40 text-white text-center text-2xl tracking-[0.5em] font-mono" data-testid="device-code-input" autoFocus />
      <div className="flex justify-between text-[11px] text-slate-400">
        <button type="button" onClick={() => { setDeviceCode(""); setDeviceVerify(null); }} className="hover:text-white" data-testid="device-verify-cancel">{t('cancel')}</button>
        <button type="button" onClick={async () => { try { await resend(); } catch (e2) { const dd = e2.response?.data?.detail; if (dd?.code === "DEVICE_VERIFY_REQUIRED") { setDeviceVerify({ ...dd, form: deviceVerify.form }); toast.success(t('deviceVerifyResent')); } else toast.error((typeof dd === "string" ? dd : dd?.message) || e2.message); } }} className="hover:text-amber-300" data-testid="device-verify-resend">{t('deviceVerifyResend')}</button>
      </div>
      <p className="text-[10px] text-slate-500">{t('deviceVerifyHelp')}</p>
    </div>
  );

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    const u = formVal(e, "username", username), p = formVal(e, "password", password);
    if (!u || !p) { toast.error(t('fillAllFields')); return; }
    setIsLoading(true);
    try {
      await login(u, p, deviceCode ? { device_code: deviceCode } : {});
      setDeviceVerify(null); setDeviceCode("");
      try { localStorage.setItem("sx_login_tab", "admin"); } catch {}
      navigate(location.state?.from || "/admin");
      toast.success(t('login') + " OK");
    } catch (error) {
      if (error.response?.status === 428 && error.response?.data?.detail?.code === "DEVICE_VERIFY_REQUIRED") {
        setDeviceVerify({ ...error.response.data.detail, form: "admin" }); setLoginError(""); setIsLoading(false); return;
      }
      const d0 = error.response?.data?.detail;
      const netMsg = isNetFail(error) ? "网络慢，连接超时，请再试一次 / Conexión lenta, inténtelo de nuevo" : "";
      const msg = netMsg || ((typeof d0 === "string" ? d0 : d0?.message) || error.message || t('login') + " failed") + (error.response?.status === 401 ? ` — ${t('adminTabMerchantHint')}` : "");
      if (isNewBrowser()) trackLanding('login_fail');
      setLoginError(msg);
      toast.error(msg);
    } finally { setIsLoading(false); }
  };

  const handleTenantLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    const tenantId = formVal(e, "tenant_id", tenantIdState), tUsername = formVal(e, "username", tUsernameState), tPassword = formVal(e, "password", tPasswordState);
    if (!tenantId || !tUsername || !tPassword) { toast.error(t('fillAllFields')); return; }
    setIsLoading(true);
    try {
      // Offline: try cached credentials
      if (!navigator.onLine) {
        const ok = await tryOfflineLogin(tenantId, tUsername, tPassword);
        if (ok) return;
        setLoginError(t('offlineNoCache') || tr('x_f134be36'));
        toast.error(t('offlineNoCache') || tr('x_f134be36'));
        setIsLoading(false);
        return;
      }
      const { user: userData, tenant } = await tenantLogin(tenantId, tUsername, tPassword, deviceCode ? { device_code: deviceCode } : {});
      setDeviceVerify(null); setDeviceCode("");
      // Cache credentials for offline login
      await saveOfflineCreds(tenantId, tUsername, tPassword);
      try { localStorage.setItem("pos_tenant_id", tenantId.trim()); localStorage.setItem("sx_login_tab", "tenant"); } catch {}
      const role = userData?.role || 'staff';
      if (role === 'price_checker' || role === 'stock_viewer') {
        navigate("/price-check");
      } else if (role === 'wholesale_clerk') {
        navigate("/admin/wholesale");
      } else if (role === 'order_taker') {
        // 📱 phone → mobile order pad; 💻 PC → full POS (pay button parks the order for the cashier, owner 2026-10-31)
        navigate(isPhoneDevice() ? "/pos/mobile" : "/pos");
      } else if (['cashier', 'staff', 'store_manager'].includes(role)) {
        navigate("/pos");
      } else {
        navigate("/admin");
      }
      toast.success(`${t('login')} OK - ${tenant?.name || ''}`);
    } catch (error) {
      // Network error fallback: try offline login
      if (isNetFail(error)) {
        const ok = await tryOfflineLogin(tenantId, tUsername, tPassword);
        if (ok) return;
        const offMsg = t('offlineNoCache') || tr('x_f134be36');
        setLoginError(offMsg);
        toast.error(offMsg);
        return;
      }
      // 🔐 new device → the server mailed a 6-digit code; show the code box instead of an error
      if (error.response?.status === 428 && error.response?.data?.detail?.code === "DEVICE_VERIFY_REQUIRED") {
        setDeviceVerify({ ...error.response.data.detail, form: "tenant" }); setLoginError(""); setIsLoading(false); return;
      }
      const d = error.response?.data?.detail;
      const msg = (typeof d === "string" ? d : d?.message) || error.message || t('login') + " failed";
      if (isNewBrowser()) trackLanding('login_fail_tenant');
      setLoginError(msg);
      toast.error(msg);
    } finally { setIsLoading(false); }
  };

  const handlePosLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    const posId = formVal(e, "tenant_id", posIdState), posUser = formVal(e, "username", posUserState), posPwd = formVal(e, "password", posPwdState);
    if (!posId || !posUser || !posPwd) { toast.error(t('fillAllFields')); return; }
    setIsLoading(true);
    try {
      if (!navigator.onLine) {
        const ok = await tryOfflineLogin(posId, posUser, posPwd);
        if (ok) return;
        setLoginError(t('offlineNoCache') || tr('x_f134be36'));
        toast.error(t('offlineNoCache') || tr('x_f134be36'));
        setIsLoading(false);
        return;
      }
      const { user: userData, tenant } = await tenantLogin(posId, posUser, posPwd);
      await saveOfflineCreds(posId, posUser, posPwd);
      try { localStorage.setItem("pos_tenant_id", posId.trim()); } catch {}
      const role = userData?.role || 'staff';
      if (role === 'price_checker' || role === 'stock_viewer') {
        navigate("/price-check");
      } else if (role === 'wholesale_clerk') {
        navigate("/admin/wholesale");
      } else if (role === 'order_taker') {
        // 📱 phone → mobile order pad; 💻 PC → full POS (pay button parks the order for the cashier, owner 2026-10-31)
        navigate(isPhoneDevice() ? "/pos/mobile" : "/pos");
      } else {
        navigate("/pos");
      }
      toast.success(`POS - ${tenant?.name || ''}`);
    } catch (error) {
      if (isNetFail(error)) {
        const ok = await tryOfflineLogin(posId, posUser, posPwd);
        if (ok) return;
        const offMsg = t('offlineNoCache') || tr('x_f134be36');
        setLoginError(offMsg);
        toast.error(offMsg);
        return;
      }
      const msg = error.response?.data?.detail || error.message || t('login') + " failed";
      setLoginError(msg);
      toast.error(msg);
    } finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src="/sellox-hero.png" alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/60" />
      </div>
      <Card className="w-full max-w-md bg-slate-800/70 border-slate-700 backdrop-blur-xl relative z-10">
        {/* Language Switcher */}
        <div className="flex justify-end gap-1 px-4 pt-3" data-testid="login-lang-switcher">
          {[
            { code: 'zh', label: '中文', flag: '🇨🇳' },
            { code: 'en', label: 'EN', flag: '🇺🇸' },
            { code: 'es', label: 'ES', flag: '🇪🇸' },
            { code: 'pt', label: 'PT', flag: '🇧🇷' },
          ].map(l => (
            <button
              key={l.code}
              type="button"
              onClick={() => changeLang(l.code)}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${lang === l.code ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-500/50' : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 border border-transparent'}`}
              data-testid={`lang-btn-${l.code}`}
              title={l.code}
            >
              <span className="flag mr-1">{l.flag}</span>{l.label}
            </button>
          ))}
        </div>
        <CardHeader className="text-center pt-3">
          <div className="mx-auto w-16 h-16 rounded-2xl overflow-hidden mb-4">
            <img src={themeIconUrl()} alt="Sellox" className="w-full h-full object-cover" />
          </div>
          <CardTitle className="text-2xl font-extrabold sx-wordmark">Sellox</CardTitle>
          <p className="text-slate-400 text-sm">Smart POS · {t('storeManagement')}</p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="w-full bg-slate-700/50 mb-4">
              <TabsTrigger value="admin" className="flex-1 data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 text-xs sm:text-sm" data-testid="tab-admin">{t('adminLogin')}</TabsTrigger>
              <TabsTrigger value="tenant" className="flex-1 data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-xs sm:text-sm" data-testid="tab-tenant">{t('tenantLogin')}</TabsTrigger>
              <TabsTrigger value="pos" className="flex-1 data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-xs sm:text-sm" data-testid="tab-pos">{t('posLogin') || tr('x_80745991')}</TabsTrigger>
            </TabsList>

            <TabsContent value="admin">
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div><label className="text-sm text-slate-300 mb-1 block">{t('username')}</label>
                  <Input name="username" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('username')} className="bg-slate-700/50 border-slate-600 text-white" data-testid="login-username" /></div>
                <div><label className="text-sm text-slate-300 mb-1 block">{t('password')}</label>
                  <div className="relative">
                    <Input name="password" autoComplete="current-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('password')} className="bg-slate-700/50 border-slate-600 text-white pr-10" data-testid="login-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors" data-testid="toggle-password-admin">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {deviceVerify && deviceVerify.form === "admin" && codeBox(() => login(username, password, {}))}
                <Button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600" disabled={isLoading || (!!deviceVerify && deviceCode.length !== 6)} data-testid="login-submit">
                  {isLoading ? t('loading') : t('login')}
                </Button>
                {loginError && <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm text-center" data-testid="login-error">{loginError}</div>}
              </form>
            </TabsContent>

            <TabsContent value="tenant">
              <form onSubmit={handleTenantLogin} className="space-y-4">
                <div><label className="text-sm text-slate-300 mb-1 block">{t('tenantIdLabel')}</label>
                  <Input name="tenant_id" autoComplete="organization" value={tenantIdState} onChange={(e) => setTenantId(e.target.value)} placeholder={t('providedByAdmin')} className="bg-slate-700/50 border-slate-600 text-white" data-testid="tenant-id-input" /></div>
                <div><label className="text-sm text-slate-300 mb-1 block">{t('username')}</label>
                  <Input name="username" autoComplete="username" value={tUsernameState} onChange={(e) => setTUsername(e.target.value)} placeholder={t('username')} className="bg-slate-700/50 border-slate-600 text-white" data-testid="tenant-username-input" /></div>
                <div><label className="text-sm text-slate-300 mb-1 block">{t('password')}</label>
                  <div className="relative">
                    <Input name="password" autoComplete="current-password" type={showTPassword ? "text" : "password"} value={tPasswordState} onChange={(e) => setTPassword(e.target.value)} placeholder={t('password')} className="bg-slate-700/50 border-slate-600 text-white pr-10" data-testid="tenant-password-input" />
                    <button type="button" onClick={() => setShowTPassword(!showTPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors" data-testid="toggle-password-tenant">
                      {showTPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {deviceVerify && deviceVerify.form === "tenant" && codeBox(() => tenantLogin(tenantIdState, tUsernameState, tPasswordState, {}))}
                <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600" disabled={isLoading || (!!deviceVerify && deviceCode.length !== 6)} data-testid="tenant-login-submit">
                  {isLoading ? t('loading') : t('login')}
                </Button>
                <button type="button" onClick={() => setShowForgot(true)} className="w-full text-center text-xs text-slate-400 hover:text-emerald-400 transition-colors" data-testid="forgot-password-link-tenant">
                  {tr('x_fa9a9ed5')}
                </button>
                {loginError && <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm text-center" data-testid="login-error">{loginError}</div>}
              </form>
            </TabsContent>

            <TabsContent value="pos">
              <form onSubmit={handlePosLogin} className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-1">
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-medium"><Monitor className="w-4 h-4" /> {t('posLoginHint') || tr('x_29ee0354')}</div>
                </div>
                <div><label className="text-sm text-slate-300 mb-1 block">{t('tenantIdLabel')}</label>
                  <Input name="tenant_id" autoComplete="organization" value={posIdState} onChange={(e) => setPosId(e.target.value)} placeholder={t('providedByAdmin')} className="bg-slate-700/50 border-slate-600 text-white" data-testid="pos-tenant-id" /></div>
                <div><label className="text-sm text-slate-300 mb-1 block">{t('username')}</label>
                  <Input name="username" autoComplete="username" value={posUserState} onChange={(e) => setPosUser(e.target.value)} placeholder={t('username')} className="bg-slate-700/50 border-slate-600 text-white" data-testid="pos-username" /></div>
                <div><label className="text-sm text-slate-300 mb-1 block">{t('password')}</label>
                  <div className="relative">
                    <Input name="password" autoComplete="current-password" type={showPosPwd ? "text" : "password"} value={posPwdState} onChange={(e) => setPosPwd(e.target.value)} placeholder={t('password')} className="bg-slate-700/50 border-slate-600 text-white pr-10" data-testid="pos-password" />
                    <button type="button" onClick={() => setShowPosPwd(!showPosPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors" data-testid="toggle-password-pos">
                      {showPosPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" disabled={isLoading} data-testid="pos-login-submit">
                  <Monitor className="w-4 h-4 mr-2" /> {isLoading ? t('loading') : (t('enterPos') || tr('x_2443e297'))}
                </Button>
                <button type="button" onClick={() => setShowForgot(true)} className="w-full text-center text-xs text-slate-400 hover:text-emerald-400 transition-colors" data-testid="forgot-password-link-pos">
                  {tr('x_fa9a9ed5')}
                </button>
                {loginError && <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm text-center" data-testid="login-error">{loginError}</div>}
              </form>
            </TabsContent>
          </Tabs>
          {!(window.selloxDesktop || navigator.userAgent.includes("Electron")) && (
            <div className="text-center mt-4 pt-3 border-t border-slate-700">
              <Link to="/register" className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="login-register-link">
                {t('regFreeTrialLink')}
              </Link>
            </div>
          )}
          {/* App install entry removed from login page per boss request (links shared privately) */}
          <div className="text-center mt-3 text-[11px] font-mono text-slate-500" data-testid="app-version-login">
            Sellox · {appVer.label}
          </div>
        </CardContent>
      </Card>
      <ForgotPasswordModal open={showForgot} onOpenChange={setShowForgot} />
      <Dialog open={showIosGuide} onOpenChange={setShowIosGuide}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm" data-testid="ios-guide-dialog">
          <DialogHeader>
            <DialogTitle className="text-blue-400">📲 iPhone 安装 / Instalar en iPhone</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3 bg-slate-900/60 rounded-lg p-3">
              <span className="text-xl">1️⃣</span>
              <p>用 <b>Safari</b> 打开 sellox.shop,点底部 <b>分享按钮</b> <span className="inline-block px-1.5 py-0.5 bg-slate-700 rounded text-xs">⬆️</span><br /><span className="text-slate-400">Abre sellox.shop en Safari y toca el botón Compartir</span></p>
            </div>
            <div className="flex items-start gap-3 bg-slate-900/60 rounded-lg p-3">
              <span className="text-xl">2️⃣</span>
              <p>下滑选择 <b>"添加到主屏幕"</b><br /><span className="text-slate-400">Selecciona "Añadir a pantalla de inicio"</span></p>
            </div>
            <div className="flex items-start gap-3 bg-slate-900/60 rounded-lg p-3">
              <span className="text-xl">3️⃣</span>
              <p>桌面出现 <b>Sellox</b> 图标,打开即是全屏 App,自动保持最新版<br /><span className="text-slate-400">El icono Sellox aparece en tu pantalla — siempre actualizado</span></p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
